<template>
  <!-- Compact mode: just prev/next arrows with page input -->
  <nav v-if="compact" class="pagination-nav is-compact">
    <button
      class="button is-small is-ghost"
      :disabled="isDisabled || !canGoPrevious"
      @click="goToPreviousPage"
    >
      <i class="fa-solid fa-backward-step" />
    </button>
    <div class="pg-compact-input">
      <input
        v-model.number="pageNumber"
        class="input is-small pg-input"
        type="number"
        min="1"
        :max="total"
        @change="goToPage(pageNumber)"
        @keyup.enter="goToPage(pageNumber)"
      />
      <span class="pg-of">/ {{ total }}</span>
    </div>
    <button
      class="button is-small is-ghost"
      :disabled="isDisabled || !canGoNext"
      @click="goToNextPage"
    >
      <i class="fa-solid fa-forward-step" />
    </button>
  </nav>

  <!-- Full mode: page list with ellipsis -->
  <PaginationRoot
    v-else
    v-model:page="currentPage"
    class="pagination-nav"
    :total="totalItems"
    :items-per-page="itemsPerPage"
    :sibling-count="siblingCount"
    show-edges
    @update:page="$emit('page-change', $event)"
  >
    <PaginationList v-slot="{ items }" class="pg-list">
      <PaginationPrev as-child>
        <button
          class="button is-small is-ghost"
          :disabled="isDisabled || !canGoPrevious"
        >
          <i class="fa-solid fa-backward-step" />
        </button>
      </PaginationPrev>
      <template v-for="(item, index) in items">
        <PaginationListItem
          v-if="item.type === 'page'"
          :key="index"
          :value="item.value"
          as-child
        >
          <button
            class="button is-small"
            :class="
              currentPage === item.value && !hasSinglePage
                ? 'is-primary'
                : 'is-outlined'
            "
            :disabled="isDisabled || hasSinglePage"
          >
            {{ item.value }}
          </button>
        </PaginationListItem>
        <PaginationEllipsis v-else :key="item.type" :index="index" as-child>
          <DropdownButton
            :show-arrow="false"
            fit-content
            align="center"
            @open="focusGoToInput"
          >
            <template #trigger>
              <button class="button is-small pg-ellipsis">&hellip;</button>
            </template>
            <div class="field has-addons mb-0 p-2">
              <div class="control">
                <input
                  :ref="
                    (el) => {
                      goToInput = el
                    }
                  "
                  v-model.number="pageNumber"
                  class="input is-small pg-input"
                  type="number"
                  min="1"
                  :max="total"
                  @focus="$event.target.select()"
                  @keyup.enter="goToPage(pageNumber)"
                />
              </div>
              <div class="control">
                <button
                  class="button is-small is-primary"
                  :disabled="!canGoTo(pageNumber)"
                  @click="goToPage(pageNumber)"
                >
                  <span class="icon is-small">
                    <i class="fa-solid fa-arrow-right" />
                  </span>
                </button>
              </div>
            </div>
          </DropdownButton>
        </PaginationEllipsis>
      </template>
      <PaginationNext as-child>
        <button
          class="button is-small is-ghost"
          :disabled="isDisabled || !canGoNext"
        >
          <i class="fa-solid fa-forward-step" />
        </button>
      </PaginationNext>
    </PaginationList>
  </PaginationRoot>
</template>

<script>
import { ref, computed, watch, nextTick } from 'vue'
import {
  PaginationRoot,
  PaginationList,
  PaginationListItem,
  PaginationEllipsis,
  PaginationPrev,
  PaginationNext,
} from 'reka-ui'
import DropdownButton from '@/components/ui/DropdownButton.vue'

export default {
  name: 'Pagination',
  components: {
    PaginationRoot,
    PaginationList,
    PaginationListItem,
    PaginationEllipsis,
    PaginationPrev,
    PaginationNext,
    DropdownButton,
  },
  props: {
    /** Current page (1-based) */
    current: { type: [Number, String], default: 1 },
    /** Total number of pages */
    total: { type: Number, default: 1 },
    /** Show compact mode (prev / input / next only) */
    compact: { type: Boolean, default: false },
    /** Number of sibling pages around current */
    siblingCount: { type: Number, default: 0 },
    disabled: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
  },
  emits: ['page-change'],
  setup(props, { emit }) {
    const currentPage = ref(parseInt(props.current, 10) || 1)
    const pageNumber = ref(currentPage.value)
    const goToInput = ref(null)
    const focusGoToInput = () => {
      nextTick(() => goToInput.value?.focus())
    }

    watch(
      () => props.current,
      (val) => {
        currentPage.value = parseInt(val, 10) || 1
      },
    )

    // Keep pageNumber in sync so the ellipsis input always shows the current page
    watch(currentPage, (val) => {
      pageNumber.value = val
    })

    const isDisabled = computed(() => props.disabled || props.loading)
    const hasSinglePage = computed(() => props.total <= 1)
    const canGoNext = computed(() => currentPage.value < props.total)
    const canGoPrevious = computed(() => currentPage.value > 1)

    // reka-ui needs total items + items per page to compute page count.
    // We already have total pages, so we fake it: total items = total pages, items per page = 1
    const totalItems = computed(() => props.total)
    const itemsPerPage = 1

    const canGoTo = (n) => n >= 1 && n <= props.total
    const goToPage = (n) => {
      n = Math.max(1, Math.min(n, props.total))
      if (canGoTo(n)) {
        currentPage.value = n
        emit('page-change', n)
      }
    }
    const goToNextPage = () =>
      canGoNext.value && goToPage(currentPage.value + 1)
    const goToPreviousPage = () =>
      canGoPrevious.value && goToPage(currentPage.value - 1)

    return {
      currentPage,
      pageNumber,
      isDisabled,
      hasSinglePage,
      canGoNext,
      canGoPrevious,
      totalItems,
      itemsPerPage,
      canGoTo,
      goToPage,
      goToInput,
      focusGoToInput,
      goToNextPage,
      goToPreviousPage,
    }
  },
}
</script>

<style lang="scss" scoped>
.pagination-nav {
  display: inline-flex;
  align-items: center;
  gap: 0;
  margin: 0;
}

.pg-list {
  display: flex;
  align-items: center;
  gap: 2px;
  list-style: none;
  margin: 0;
  padding: 0;
}

// Ellipsis — dashed border to distinguish from page buttons
.pg-ellipsis {
  border-style: dashed;
  padding-inline: 0.4em;
  letter-spacing: 0.1em;
}

.pg-compact-input {
  display: inline-flex;
  align-items: center;
  gap: 0.25em;
}

.pg-of {
  font-size: 0.85em;
  color: var(--text-light, hsl(0, 0%, 48%));
  white-space: nowrap;
}

// Compact variant
.is-compact {
  gap: 0.2em;
}
</style>

<!-- Unscoped: .pg-input is also used inside portaled dropdown -->
<style lang="scss">
input.pg-input[type='number'] {
  width: 3.5em !important;
  text-align: center;
  -moz-appearance: textfield !important;
  appearance: textfield !important;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none !important;
    display: none !important;
    margin: 0;
  }
}
</style>
